import Handlebars from "handlebars";
import _ from "underscore";

/**
 * Assigns Backbone.View.tagName
 * @param tag
 * @returns {decorator}
 */
export function tagName(tag) {
    return function decorator(target) {
        target.prototype.tagName = tag;
    };
}

/**
 * Assigns Backbone.View.className
 * @param htmlClass
 * @returns {decorator}
 */
export function className(htmlClass) {
    return function decorator(target) {
        target.prototype.className = htmlClass;
    };
}

/**
 * Assigns Backbone.View.domain
 * @param domain
 * @returns {decorator}
 */
export function domain(domain) {
    return function decorator(target) {
        target.prototype.domain = domain;
    };
}

/**
 * Assigns a Backbone Model to target
 * @param className {Backbone.Model}
 * @param [opts] {object}
 * @returns {decorator}
 */
export function model(className, opts) {
    return function decorator(target) {
        target.prototype.model = opts ? new className(opts) : new className();
    };
}

/**
 * Assigns a Handlebars template to Backbone.View.template, after compiling
 * @param hbsTemplate
 * @param [opts] {object}
 * @returns {decorator}
 */
export function template(hbsTemplate, opts) {
    return function decorator(target) {
        const args = [hbsTemplate];
        if (opts) args.push(opts);
        target.prototype.template = Handlebars.compile.apply(this, args);
    };
}

/**
 * Assigns Backbone.View.el
 * @param value
 * @returns {decorator}
 */

export function el(value) {
    return function decorator(target) {
        target.prototype.el = value;
    };
}

/**
 * Binds a custom event to the target function
 * @param eventName {string}
 * @returns {object}
 */
export function handles(eventName) {
    return function decorator(target, name, descriptor) {

        target.customEvents = _.extend({}, target.customEvents, target.__proto__.customEvents);

        if (_.isFunction(target.customEvents)) throw new Error('The handles decorator is not compatible with an events method');

        if (!eventName) throw new Error('The handles decorator requires an eventName argument');

        const targetCb = descriptor.value;

        if (_.isFunction(targetCb))
            target.customEvents[eventName] = targetCb;
        else
            throw new Error(`target.${name} is not a function to be used as a callback method`);

        return descriptor;
    };
}

/**
 * Binds an event to the target function
 * If partialArgs is empty, the target function name is passed to the events hash
 * else the target function value is used to apply the passed arguments via _.partial
 * @param eventName {string}
 * @param partialArgs Array
 * @returns {object}
 */
export function on(eventName, ...partialArgs) {
    return function decorator(target, name, descriptor) {

        // console.info(`Binding event: ${eventName} to ${target.constructor.name}`);

        const preExistingEvents = _.isFunction(target.events) ? target.events() : target.events;

        target.events = _.extend({}, preExistingEvents, target.__proto__.events);

        if (!eventName) {
            throw new Error('The on decorator requires an eventName argument');
        }

        target.events[eventName] = partialArgs.length ? _.partial(descriptor.value, ...partialArgs) : name;

        return descriptor;
    };
}

/**
 * Debounces target function by given time
 * @param time - in milliseconds
 * @returns {object}
 */
export function debounce(time) {
    return function decorator(target, name, descriptor) {
        descriptor.value = _.debounce(descriptor.value, time);
        return descriptor;
    };
}

/**
 * Memoizes target function
 * @param [hasher] {function}
 * @returns {object}
 */
export function memoize(hasher) {
    return function decorator(target, name, descriptor) {
        descriptor.value = _.memoize(descriptor.value, hasher);
        return descriptor;
    };
}

/**
 * Asserts that the function will only run once
 * @returns {object}
 */
export function once() {
    return function decorator(target, name, descriptor) {
        descriptor.value = _.once(descriptor.value);
        return descriptor;
    };
}

/**
 * Mixes a prototype object into target
 * @param prototype {object}
 * @returns {object}
 */
export function mixin(prototype) {
    return function decorator(target) {
        _.extend(target.prototype, prototype);
    };
}

/**
 * Partial application of function arguments
 * @param args {object[]}
 * @returns {object}
 */
export function partial(args) {
    return function decorator(target, name, descriptor) {
        descriptor.value = _.partial(descriptor.value, ...args);
        return descriptor;
    };
}